/**
 * 配置来源（优先级从高到低）：
 * 1. 浏览器全局 `window.__RUNTIME_CONFIG__` — Docker/K8s 启动时由 `docker/docker-entrypoint.sh` 根据环境变量
 *    `APP_BASE_URL`、`APP_WEBSOCKET_URL`、`APP_ID`、`APP_KEY`、`APP_LICENSE`、`APP_AMAP_KEY` 生成 `runtime-config.js`
 * 2. Vite 环境变量 `import.meta.env.VITE_*` — 构建时写入（docker build --build-arg / CI 中 export）
 * 3. 下方 DEFAULT_CONFIG 默认值
 *
 * 本地开发：在 `env/.env` 或 `env/.env.local` 中设置 VITE_*（见 env/.env.example）
 */

import type { AppConfig } from './config.types'

/** 与 `cloud-sdk` 中 `WebSocketConfiguration#registerStompEndpoints` 一致（STOMP 端点） */
export const WEBSOCKET_STOMP_PATH = '/api/v1/ws'

/** 与后端、Pilot 等约定：HTTP(S) base 必须以 / 结尾 */
function ensureTrailingSlash (url: string): string {
  if (!url) return url
  return url.endsWith('/') ? url : `${url}/`
}

/** 仅当未单独配置 WebSocket 时，由 HTTP base 推导 */
function httpBaseToWebSocketUrl (baseURL: string): string {
  const u = baseURL.replace(/\/$/, '')
  if (u.startsWith('https://')) {
    return `${u.replace('https://', 'wss://')}${WEBSOCKET_STOMP_PATH}`
  }
  return `${u.replace('http://', 'ws://')}${WEBSOCKET_STOMP_PATH}`
}

const DEFAULT_CONFIG: AppConfig = {
  appId: '180915',
  appKey: 'ca79857781c83306fab273a48ce57f2',
  appLicense:
    'VAqyB6kkGUCuKkuXsAwQgkVxE1NRkzvxR0UYQOBbBJZzdpu4u+tfG4Y5PZpfQjQRcIyFLcGdLQuwDogzBXc68fU43mTskyK/MTGQ9CwL34BmZWPbY4xZUTOWQylYxyTK1JeO33Ogzcf1wOvEXcaOxsqQCh5hsn6xkNNw8eHfJ00=',

  baseURL: 'http://192.168.1.13:6789/',
  websocketURL: 'ws://192.168.1.13:6789/api/v1/ws',

  rtmpURL: 'Please enter the rtmp access address.',
  gbServerIp: 'Please enter the server ip.',
  gbServerPort: 'Please enter the server port.',
  gbServerId: 'Please enter the server id.',
  gbAgentId: 'Please enter the agent id',
  gbPassword: 'Please enter the agent password',
  gbAgentPort: 'Please enter the local port.',
  gbAgentChannel: 'Please enter the channel.',
  rtspUserName: 'Please enter the username.',
  rtspPassword: 'Please enter the password.',
  rtspPort: '8554',
  agoraAPPID: 'Please enter the agora app id.',
  agoraToken: 'Please enter the agora temporary token.',
  agoraChannel: 'Please enter the agora channel.',
  amapKey: '3eb1941b2dad7f34801b300d5328ee17',
}

function envString (key: string): string | undefined {
  const v = (import.meta.env as Record<string, string | undefined>)[key]
  if (v === undefined || v === null || String(v).trim() === '') return undefined
  return String(v)
}

function buildFromViteEnv (): Partial<AppConfig> {
  const out: Partial<AppConfig> = {}
  const appId = envString('VITE_APP_ID')
  const appKey = envString('VITE_APP_KEY')
  const appLicense = envString('VITE_APP_LICENSE')
  const baseURL = envString('VITE_APP_BASE_URL')
  const websocketURL = envString('VITE_APP_WEBSOCKET_URL')
  const amapKey = envString('VITE_APP_AMAP_KEY')

  if (appId) out.appId = appId
  if (appKey) out.appKey = appKey
  if (appLicense) out.appLicense = appLicense
  if (baseURL) {
    out.baseURL = ensureTrailingSlash(baseURL)
    if (!websocketURL) {
      out.websocketURL = httpBaseToWebSocketUrl(out.baseURL)
    }
  }
  if (websocketURL) out.websocketURL = websocketURL
  if (amapKey) out.amapKey = amapKey

  const rtmp = envString('VITE_APP_RTMP_URL')
  if (rtmp) out.rtmpURL = rtmp
  return out
}

function getRuntimeOverrides (): Partial<AppConfig> {
  if (typeof window === 'undefined') return {}
  const r = window.__RUNTIME_CONFIG__
  if (!r || typeof r !== 'object') return {}
  const out: Partial<AppConfig> = { ...(r as Partial<AppConfig>) }
  if (out.baseURL) out.baseURL = ensureTrailingSlash(out.baseURL)
  if (out.baseURL && !out.websocketURL) {
    out.websocketURL = httpBaseToWebSocketUrl(out.baseURL)
  }
  return out
}

/** 合并：默认 < Vite 构建时环境变量 < 运行时注入 */
export const CURRENT_CONFIG: AppConfig = {
  ...DEFAULT_CONFIG,
  ...buildFromViteEnv(),
  ...getRuntimeOverrides(),
}
